/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */

import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    Input,
    OnDestroy,
    QueryList,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { BaseComponent } from '../../core/components';
import { ExternalPageContentBodyComponent } from './external-page-content-body.component';
import { ExternalPageContentFooterComponent } from './external-page-content-footer.component';
import { ModalService } from '../../core/services';

@Component({
    selector: 'app-external-page',
    templateUrl: './external-page-wrapper.component.html',
    styleUrls: ['../externalPageStyles.scss']
})
export class ExternalPageWrapperComponent extends BaseComponent implements AfterViewInit, OnDestroy {
    /**
     * Get modal body component content
     */
    @ContentChildren(ExternalPageContentBodyComponent, { descendants: true })
    public modalBodyContent: QueryList<ExternalPageContentBodyComponent>;

    /**
     * Get modal footer component content
     */
    @ContentChildren(ExternalPageContentFooterComponent, { descendants: true })
    public modalFooterContent: QueryList<ExternalPageContentFooterComponent>;

    /**
     * Marketing block element
     * @private
     */
    @ViewChild('marketingElement', { static: true })
    private marketingElementRef: ElementRef;

    /**
     * Marketing block element
     * @private
     */
    @ViewChild('contentElement', { static: true })
    private contentElementRef: ElementRef;

    /**
     * First tagline to show on the left of the external pages on the background image.
     */
    @Input() public marketingBlock: TemplateRef<any> = null;

    /**
     * Optional hide marketing block completely.
     */
    @Input() public hideMarketingBlock: boolean = false;

    /**
     * Ability to override the height of the content
     */
    @Input() public contentHeight: number;

    /**
     * Ability to override the width of the content
     */
    @Input() public contentWidth: number;

    /**
     * Ability to a class to the content.
     */
    @Input() public additionalClass: string;

    /**
     * Ability to override the height of the form block footer
     */
    @Input() public formBlockFooterHeight: number = 80;

    /**
     * Displays the current year in the copyright and version footer.
     */
    public currentYear = new Date().getFullYear();

    /**
     * Store the status of 'better up-time'.
     */
    public betterUpTimeStatus: 'pending' | 'paused' | 'maintenance' | 'up' | 'validating' | 'down';

    /**
     * Store the modal title set in open policy modal.
     */
    public modalTitle: string;

    /**
     * Global set based on content of the footer children
     */
    public footerHasContent: boolean = false;

    /**
     * Show or hide the marketing block depending on content width.
     */
    public showMarketingBlock: boolean = false;

    /**
     * Logo path based on environment file.
     */
    public logoPath: string = this.environment.module === this.modulesEnum.DEEPTHOUGHT
        ? 'assets/shared/logos/portal-admin-logo.svg'
        : 'assets/shared/logos/logo-white-text.svg';

    constructor(
        private cdr: ChangeDetectorRef,
        private modalService: ModalService
    ) {
        super();
    }

    ngAfterViewInit(): void {
        this.footerHasContent = !!this.modalFooterContent.toArray()[0];
        const marketingBlockWidth = this.marketingElementRef?.nativeElement?.offsetWidth || 412; // CSS size;
        const pageContentWidth = this.contentElementRef?.nativeElement?.offsetWidth;

        if (!this.hideMarketingBlock) {
            // Set initial state and listener.
            this.setShowMarketingBlock(marketingBlockWidth, pageContentWidth);
            window.addEventListener('resize', () => this.setShowMarketingBlock(marketingBlockWidth, pageContentWidth), true);

            this.cdr.detectChanges();
        }
    }

    ngOnDestroy(): void {
        window.removeEventListener('resize', () => this.setShowMarketingBlock(0, 0), true);
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    /**
     * Click handler to open modal.
     * @param policy
     */
    public openPolicyModal(policy: string): void {
        const modalId: 'service' | 'privacy' = policy === 'policies_terms_of_service' ? 'service' : 'privacy';
        this.modalService.openModalInParent(modalId);
    }

    /**
     * Set the show marketing block variable.
     * @private
     */
    private setShowMarketingBlock(marketingBlockWidth: number, pageContentWidth: number): void {
        const bodyWidth: number = document.body.clientWidth;
        this.showMarketingBlock = marketingBlockWidth + pageContentWidth + 50 < bodyWidth;
    }
}
